/**
 * JIS配列キーボードのヒートマップ(docs/07_learning_support.md §4.2「苦手キー分析」)。
 *
 * プレイ中の `KeyboardGuide` と同じ `logic/keyboard.ts` のキー定義を使い、
 * 色だけ「指の色」から「ミス率の濃淡」に差し替えたもの。
 * まだ打鍵数が少ないキーは灰色にして、統計として断定しない。
 */

import { useMemo } from 'react';
import { KEYBOARD_ROWS, keyLabel, type KeyDef } from '../../logic/keyboard';
import type { KeyHeat } from '../../logic/weakKeys';

const UNIT = 64;
const GAP = 6;
const ROW_HEIGHT = 62;
const ROW_GAP = 6;

/** ミス率 → 色。低いほど緑、高いほど赤。 */
export function heatColor(missRate: number): string {
  if (missRate < 0.05) return '#51cf66';
  if (missRate < 0.12) return '#a9e34b';
  if (missRate < 0.2) return '#ffd43b';
  if (missRate < 0.32) return '#ff922b';
  return '#ff4757';
}

/** 凡例(下に並べる帯)。 */
export const HEAT_LEGEND: readonly { readonly color: string; readonly label: string }[] = [
  { color: '#51cf66', label: 'ばっちり' },
  { color: '#a9e34b', label: 'いいかんじ' },
  { color: '#ffd43b', label: 'あと すこし' },
  { color: '#ff922b', label: 'のびしろ' },
  { color: '#ff4757', label: 'のびしろ大' },
  { color: '#ced4da', label: 'まだ すくない' },
];

interface PlacedKey {
  readonly key: KeyDef;
  readonly x: number;
  readonly y: number;
  readonly width: number;
}

function layout(): {
  readonly keys: readonly PlacedKey[];
  readonly width: number;
  readonly height: number;
} {
  const rows = KEYBOARD_ROWS.map((row) => {
    let x = 0;
    const placed = row.map((key) => {
      const width = (key.width ?? 1) * UNIT;
      const item = { key, x, width };
      x += width + GAP;
      return item;
    });
    return { placed, width: Math.max(0, x - GAP) };
  });

  const width = Math.max(...rows.map((row) => row.width));
  const keys: PlacedKey[] = [];
  rows.forEach((row, rowIndex) => {
    const offset = rowIndex === KEYBOARD_ROWS.length - 1 ? (width - row.width) / 2 : 0;
    for (const item of row.placed) {
      keys.push({
        key: item.key,
        x: item.x + offset,
        y: rowIndex * (ROW_HEIGHT + ROW_GAP),
        width: item.width,
      });
    }
  });
  return { keys, width, height: KEYBOARD_ROWS.length * (ROW_HEIGHT + ROW_GAP) - ROW_GAP };
}

const LAYOUT = layout();

interface Props {
  /** `KeyDef.id` → ミス率。`logic/weakKeys.keyHeatByKeyId` の戻り値。 */
  readonly heat: ReadonlyMap<string, KeyHeat>;
  /** 強調するキーID(のびるキー TOP5)。 */
  readonly highlight?: ReadonlySet<string>;
}

export function KeyboardHeatmap({ heat, highlight }: Props) {
  const marked = useMemo(() => highlight ?? new Set<string>(), [highlight]);

  return (
    <svg
      className="keyboard-heatmap"
      viewBox={`-4 -4 ${LAYOUT.width + 8} ${LAYOUT.height + 8}`}
      role="img"
      aria-label="キーごとの れんしゅう ぐあい"
    >
      {LAYOUT.keys.map(({ key, x, y, width }) => {
        const stat = heat.get(key.id);
        const untried = stat === undefined;
        const tentative = stat?.tentative ?? true;
        const color = untried || tentative ? '#ced4da' : heatColor(stat.missRate);
        const on = marked.has(key.id);
        const label = keyLabel(key);
        const lines = label.split('\n');
        return (
          <g key={key.id} className={`heat-key ${on ? 'marked' : ''}`}>
            <rect
              x={x}
              y={y}
              width={width}
              height={ROW_HEIGHT}
              rx={10}
              fill={color}
              fillOpacity={untried ? 0.25 : tentative ? 0.45 : 0.92}
              stroke={on ? '#1b2430' : color}
              strokeWidth={on ? 5 : 2}
            />
            {lines.map((line, index) => (
              <text
                key={index}
                x={x + width / 2}
                y={y + (lines.length === 1 ? ROW_HEIGHT / 2 + 9 : 24 + index * 24)}
                textAnchor="middle"
                className="heat-label"
              >
                {line === ' ' ? '' : line}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
}
